import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Card, CardContent, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { accountingApi } from '@/api/accounting'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import type { AccountingSummary } from '@/types/accounting'

export function SummaryTab() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [from, setFrom] = useState<Dayjs>(dayjs().startOf('month'))
  const [to, setTo] = useState<Dayjs>(dayjs().endOf('month'))
  const [summary, setSummary] = useState<AccountingSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (f: Dayjs, tt: Dayjs) => {
      setLoading(true)
      setError(null)
      try {
        setSummary(await accountingApi.summary({ from: f.format('YYYY-MM-DD'), to: tt.format('YYYY-MM-DD') }))
      } catch (err) {
        setError(getErrorMessage(err) || t('common.error'))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    void load(from, to)
  }, [load, from, to])

  const cards: Array<{ key: string; label: string; value: string | undefined; highlight?: boolean }> = [
    { key: 'gross', label: t('accounting.summary.gross', 'Валовый доход'), value: summary?.gross },
    { key: 'tax', label: t('accounting.summary.tax', 'Налог'), value: summary?.tax },
    { key: 'expenses', label: t('accounting.summary.expenses', 'Расходы'), value: summary?.expenses },
    { key: 'net', label: t('accounting.summary.net', 'Чистый доход'), value: summary?.net, highlight: true },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <DatePicker
          label={t('accounting.from', 'С')}
          value={from}
          onChange={(d) => d && setFrom(d)}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label={t('accounting.to', 'По')}
          value={to}
          onChange={(d) => d && setTo(d)}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Stack>

      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={c.key}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {c.label}
                </Typography>
                {loading ? (
                  <Skeleton width={110} height={40} />
                ) : (
                  <Typography variant="h5" color={c.highlight ? 'primary.main' : undefined} fontWeight={c.highlight ? 700 : 400}>
                    {formatMoney(c.value, summary?.currency)}
                  </Typography>
                )}
                {c.key === 'tax' && summary && (
                  <Typography variant="caption" color="text.secondary">
                    {t(`accounting.regime.${summary.regime}`, summary.regime)} · {summary.tax_rate}%
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {summary && summary.expenses_by_category.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('accounting.summary.byCategory', 'Расходы по категориям')}
            </Typography>
            <Stack spacing={1}>
              {summary.expenses_by_category.map((row) => (
                <Box key={row.category} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>{t(`accounting.category.${row.category}`, row.category)}</Typography>
                  <Typography fontWeight={600}>{formatMoney(row.amount, summary.currency)}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </LocalizationProvider>
  )
}
